import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { revokeDevice } from '@/api/endpoints/devices';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';

interface RevokeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceUid: string;
  serialNumber: string;
}

/**
 * Revoking is terminal and cannot be undone from this interface: the device's
 * telemetry is rejected outright from then on, which is the correct response to a
 * stolen unit — a device credential can fabricate a crash and summon an ambulance.
 * Typing the serial number is the friction that stops this being a stray click.
 */
export function RevokeDialog({ open, onOpenChange, deviceUid, serialNumber }: RevokeDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');

  const mutation = useMutation({
    mutationFn: () => revokeDevice(deviceUid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      await queryClient.invalidateQueries({ queryKey: queryKeys.deviceStatus });
      toast.success('Device revoked. It can no longer report telemetry.');
      onOpenChange(false);
      navigate('/vehicles');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const matches = confirmation.trim().toUpperCase() === serialNumber.toUpperCase();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Report this device lost or stolen?"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!matches}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Revoke permanently
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2 text-[13px] text-ink-secondary">
          <p>
            This permanently blocks <span className="font-medium text-ink">{serialNumber}</span>.
            Anything it sends afterwards is rejected, so a stolen device cannot raise a false
            emergency.
          </p>
          <p className="font-medium text-ink">This cannot be undone. The device cannot be reused.</p>
        </div>

        <TextField
          label={`Type ${serialNumber} to confirm`}
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
    </Dialog>
  );
}
